import { volleAnalyseKindErsetzbar } from '@awf/shared';
import { type ReactNode, useRef, useState } from 'react';
import { Link } from 'react-router';
import { ApiError, type starteVolleAnalyse } from '../api/client.js';
import { ALLGEMEINER_FEHLERTEXT } from '../api/errors.js';
import { ErrorState } from '../ui/states.js';
import type { AuftragsQuelle } from './runs-source.js';
import { useAuftrag } from './use-runs.js';
import stile from './volle-analyse-knopf.module.css';

/**
 * "Dennoch volle Analyse durchführen" bzw. "Zur vollen Analyse" (Spec 2026-11-03-kein-bedarf, 4.2).
 * Gemeinsam fuer die PDP-Karte "Kein Optimierungsbedarf" und jeden Eintrag der geprueften Seiten
 * einer Ratgeber-Analyse. `url` nur beim Ratgeber-Eintrag; beim PDP nimmt das Backend die
 * Befund-URL.
 */
// Dieselbe Signatur wie der Client: `starteVolleAnalyse(runId, url?)` schickt `url` als `{ url }`
// im Body. An den Client gekoppelt, damit eine Vertragsaenderung dort hier nicht still durchrutscht.
export type StarteVolleAnalyse = typeof starteVolleAnalyse;

function ZurVollenAnalyse({ runId, klein }: { runId: string; klein: boolean }) {
  return (
    <Link
      className={`eds-button eds-button--primary${klein ? ' eds-button--sm' : ''}`}
      to={`/auftraege/${encodeURIComponent(runId)}`}
    >
      Zur vollen Analyse
    </Link>
  );
}

/**
 * Beobachtet das vermerkte Kind mit derselben Logik wie Detailseite und Seitenliste (`useAuftrag`,
 * dort `KindBeobachter`): Solange sein Stand laedt oder unbekannt ist, steht der Link. Erst ein
 * Kind, das nach `volleAnalyseKindErsetzbar` ersetzt werden darf (gescheitert, abgebrochen,
 * ausgeblendet oder dem Server unbekannt), gibt den Button wieder frei -- dieselbe Regel wie im
 * Backend.
 */
function KindAktion({
  quelle,
  runId,
  knopf,
  klein,
  klasse,
}: {
  quelle: AuftragsQuelle;
  runId: string;
  knopf: ReactNode;
  klein: boolean;
  klasse: string | undefined;
}) {
  const zustand = useAuftrag(quelle, runId);
  let inhalt: ReactNode = <ZurVollenAnalyse runId={runId} klein={klein} />;
  if (zustand.art === 'daten' && volleAnalyseKindErsetzbar(zustand.auftrag)) {
    // Ohne Knopf (fremder Auftrag) fuehrt der Link wenigstens zum gescheiterten Kind, solange es
    // noch sichtbar ist.
    inhalt = knopf ?? (zustand.auftrag ? inhalt : null);
  }
  // Ohne Knopf und ohne Link bleibt kein leerer Aktionsbereich stehen.
  return inhalt ? <div className={klasse}>{inhalt}</div> : null;
}

export function VolleAnalyseKnopf({
  ursprungRunId,
  url,
  kindRunId: vermerkt,
  starte,
  quelle,
  fremd,
  klein = false,
}: {
  ursprungRunId: string;
  url?: string;
  /** Das am Ursprung vermerkte Kind (Firestore-Stand). */
  kindRunId?: string | undefined;
  starte: StarteVolleAnalyse;
  quelle: AuftragsQuelle;
  /** Gesamtansicht (`admin`/`technik`): Das Backend startet nur fuer den eigenen Auftrag. */
  fremd: boolean;
  klein?: boolean;
}) {
  // Die Antwort des eigenen Klicks gilt vor dem Firestore-Stand: Ersetzt der Klick ein
  // gescheitertes Kind, meldet der Ursprung die neue Kennung erst etwas spaeter. `basis` merkt sich
  // den Vermerk zum Zeitpunkt des Klicks: Aendert er sich danach (eigener Klick angekommen oder ein
  // anderer Tab hat ersetzt), gilt wieder der Firestore-Stand. Der gesamte Zustand gehoert zu
  // einem Ursprung und einer Seite: Aufrufer setzen `key` darauf, React beginnt dann neu.
  const [gestartet, setGestartet] = useState<{ kind: string; basis: string | undefined }>();
  const [sendet, setSendet] = useState(false);
  const [fehler, setFehler] = useState<string>();
  // Sperre gegen den Doppelklick, bevor React `disabled` gerendert hat.
  const laeuft = useRef(false);
  const kindRunId = gestartet && gestartet.basis === vermerkt ? gestartet.kind : vermerkt;
  const klasse = stile.aktion;

  function klick() {
    if (laeuft.current) return;
    laeuft.current = true;
    setSendet(true);
    setFehler(undefined);
    const basis = vermerkt;
    (url === undefined ? starte(ursprungRunId) : starte(ursprungRunId, url)).then(
      ({ kindRunId: neu }) => {
        laeuft.current = false;
        setSendet(false);
        setGestartet({ kind: neu, basis });
      },
      (f: unknown) => {
        laeuft.current = false;
        setSendet(false);
        setFehler(f instanceof ApiError ? f.text : ALLGEMEINER_FEHLERTEXT);
      },
    );
  }

  const knopf = fremd ? null : (
    <>
      <button
        className={`eds-button eds-button--secondary${klein ? ' eds-button--sm' : ''}`}
        type="button"
        disabled={sendet}
        onClick={klick}
      >
        Dennoch volle Analyse durchführen
      </button>
      {fehler && <ErrorState text={fehler} />}
    </>
  );

  if (kindRunId) {
    return (
      <KindAktion
        key={kindRunId}
        quelle={quelle}
        runId={kindRunId}
        knopf={knopf}
        klein={klein}
        klasse={klasse}
      />
    );
  }
  return knopf ? <div className={klasse}>{knopf}</div> : null;
}
